import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import { logout } from '../../../core/store/slices/userSlice';
import SessionService from '../../../shared/services/sessionService';
import useUserProfileSync from '../../../shared/hooks/useUserProfileSync';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import SupportModal from './components/SupportModal';
import SettingsModal from './components/SettingsModal';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import { roleLabelFromStatsT } from './roleLabel';
import { isPanelTeamMember } from '../../panel/panelAccess/panelRoleAccess';
import { downloadLauncher } from '../../../shared/services/launcherService';

const Dashboard: React.FC = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const { t } = usePageTranslation('game', 'Dashboard');
  const { t: tHeader } = usePageTranslation('game', 'DashboardHeader');
  const { t: tStats } = usePageTranslation('game', 'StatsSection');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => new Date());
  const [showSettings, setShowSettings] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useUserProfileSync(5000, true);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const handleLogout = async () => {
    try {
      const sessionService = SessionService.getInstance();
      await sessionService.logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      dispatch(logout());
      localStorage.removeItem('token');
      navigate('/');
    }
  };

  const headerClock = now.toLocaleString('fr-FR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  if (!currentUser) {
    return null;
  }

  const roleLabel = roleLabelFromStatsT(currentUser.roles_id, tStats);
  const canDownloadLauncher = currentUser.roles_id != null && isPanelTeamMember(currentUser.roles_id);

  const handleDownloadLauncher = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadLauncher();
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : t('launcher.error'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 overflow-hidden bg-cockpit-bg/92 font-inter text-cockpit-text">
      <BackgroundImage />

      <header className="absolute inset-x-0 top-0 z-[1200] border-b border-cockpit-line bg-gradient-to-b from-[#16223a] to-[#0a111d] backdrop-blur-md shadow-[0_6px_24px_rgba(0,0,0,0.5)]">
        <div className="flex min-h-14 flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2 sm:px-5">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 sm:gap-4">
            <Link to="/" className="flex shrink-0 items-center gap-2.5">
              <img src="/images/logo.png" alt="Logo" className="h-9 w-9 rounded-md object-contain ring-1 ring-cockpit-line" />
              <div className="min-w-0 hidden sm:block">
                <p className="text-[9px] uppercase tracking-[0.22em] text-cockpit-muted">Tableau de bord</p>
                <p className="truncate text-sm font-bold tracking-wide text-cockpit-text">OPSECOURS WEB</p>
              </div>
            </Link>
          </div>

          <div className="flex w-full max-w-full flex-none flex-wrap items-center justify-end gap-3 sm:w-auto sm:max-w-[75%] lg:max-w-[70%]">
            <div className="flex w-full max-w-full shrink-0 flex-wrap items-center justify-end gap-2 sm:w-auto sm:max-w-none sm:flex-nowrap sm:gap-2.5">
              <div
                className="flex min-w-0 max-w-full flex-1 justify-end sm:flex-initial sm:max-w-[min(100%,280px)] lg:max-w-[320px]"
                aria-label={t('header.clockHint')}
              >
                <time
                  dateTime={now.toISOString()}
                  className="w-full truncate rounded-md border border-cockpit-line bg-cockpit-panel/60 px-3 py-2 text-center font-mono text-[10px] font-medium leading-snug text-cockpit-text-2 sm:w-auto sm:text-[11px]"
                  title={t('header.clockHint')}
                >
                  {headerClock}
                </time>
              </div>

              {currentUser.roles_id != null && isPanelTeamMember(currentUser.roles_id) && (
                <nav className="flex shrink-0 items-center justify-end" aria-label={t('header.navPanel')}>
                  <button
                    type="button"
                    onClick={() => navigate('/panel')}
                    className="flex items-center gap-2 rounded-md border border-[#6366f1]/55 bg-[#6366f1]/18 px-3 py-2 text-sm font-medium text-[#c7d2fe] transition hover:bg-[#6366f1]/30"
                  >
                    <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                      />
                    </svg>
                    {tHeader('panel')}
                  </button>
                </nav>
              )}

              <nav
                className="flex shrink-0 flex-wrap items-center justify-end gap-2"
                aria-label={t('header.navAssistance')}
              >
                <button
                  type="button"
                  onClick={() => setShowSupport(true)}
                  className="rounded-md border border-cockpit-line bg-cockpit-panel/60 px-3 py-2 text-sm font-medium text-cockpit-text-2 transition hover:border-cockpit-blue/50 hover:bg-cockpit-panel-2 hover:text-cockpit-text"
                >
                  {tHeader('support')}
                </button>
              </nav>
            </div>

            <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto sm:gap-3">


              <span className="hidden h-8 w-px shrink-0 bg-cockpit-line md:inline" aria-hidden />

              <nav className="flex flex-wrap items-center justify-end gap-2 sm:gap-2.5" aria-label={t('header.sessionNav')}>
                <button
                  type="button"
                  onClick={() => setShowSettings(true)}
                  className="rounded-md border border-cockpit-line bg-cockpit-panel/60 px-3 py-2 text-sm font-medium text-cockpit-text-2 transition hover:border-cockpit-blue/50 hover:bg-cockpit-panel-2 hover:text-cockpit-text"
                >
                  {tHeader('settings')}
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded-md border border-cockpit-red/40 bg-cockpit-red/10 px-3 py-2 text-sm font-medium text-cockpit-red transition hover:bg-cockpit-red/20"
                >
                  {tHeader('logout')}
                </button>
              </nav>

              <div className="ml-0 hidden max-w-[160px] truncate rounded-md border border-cockpit-green/35 bg-cockpit-green/10 px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-cockpit-green sm:ml-1 sm:inline lg:max-w-[200px]">
                {currentUser.username}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="absolute inset-x-0 bottom-0 overflow-y-auto p-3 sm:p-4" style={{ top: '72px' }}>
        <div className="mx-auto flex min-h-full max-w-[720px] items-center">
          <section className="relative w-full overflow-hidden rounded-lg border border-cockpit-line/70 bg-cockpit-panel/70 shadow-2xl shadow-black/40 backdrop-blur-md">
            <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-cockpit-green/12 via-transparent to-cockpit-violet/12" />
            <div className="relative border-b border-cockpit-line/60 bg-cockpit-bg/55 px-4 py-5 sm:px-8">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-cockpit-green">{t('launcher.tagline')}</p>
              <h2 className="mt-2 text-2xl font-bold tracking-tight text-cockpit-text sm:text-3xl">
                {t('welcome')},{' '}
                <span className="bg-gradient-to-r from-[#6ee7b7] to-[#5eead4] bg-clip-text text-transparent">
                  {currentUser.username}
                </span>
              </h2>
              <p className="mt-1 text-xs font-medium text-cockpit-muted">{roleLabel}</p>
            </div>

            <div className="relative flex flex-col gap-5 p-4 sm:p-8">
              <p className="text-sm leading-relaxed text-cockpit-text-2">{t('launcher.description')}</p>

              {canDownloadLauncher ? (
                <button
                  type="button"
                  onClick={handleDownloadLauncher}
                  disabled={downloading}
                  className="w-full rounded-md border border-cockpit-green/50 bg-cockpit-green/20 px-4 py-4 text-center shadow-lg shadow-emerald-900/25 transition hover:bg-cockpit-green/30 disabled:cursor-wait disabled:opacity-60"
                >
                  <span className="block text-sm font-bold uppercase tracking-[0.08em] text-cockpit-green">
                    {downloading ? t('launcher.downloading') : t('launcher.button')}
                  </span>
                  <span className="mt-1 block text-[11px] text-cockpit-muted">{t('launcher.platform')}</span>
                </button>
              ) : (
                <div className="w-full rounded-md border border-cockpit-amber/40 bg-cockpit-amber/10 px-4 py-4 text-center">
                  <span className="block text-sm font-semibold text-cockpit-amber">{t('launcher.teamOnly')}</span>
                  <span className="mt-1 block text-[11px] text-cockpit-muted">{t('launcher.teamOnlyHint')}</span>
                </div>
              )}

              {downloadError && (
                <p className="rounded-md border border-cockpit-red/40 bg-cockpit-red/10 px-3 py-2 text-sm text-cockpit-red">{downloadError}</p>
              )}

              {canDownloadLauncher && (
              <ol className="list-decimal space-y-1.5 pl-5 text-sm text-cockpit-text-2">
                <li>{t('launcher.steps.download')}</li>
                <li>{t('launcher.steps.run')}</li>
                <li>{t('launcher.steps.login')}</li>
              </ol>
              )}
            </div>
          </section>
        </div>
      </div>

      <SupportModal isOpen={showSupport} onClose={() => setShowSupport(false)} />

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        currentUser={currentUser}
      />

    </div>
  );
};

export default Dashboard;
